/**
 * Panel component following design brief
 * Used for feedback, information, and prompts
 * Features left accent bar for severity indication
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { theme } from '@core/theme';
import type { ConsequenceSeverity } from '@core/types';

export interface PanelConfig {
  width?: number;
  maxWidth?: number;
  padding?: number;
  severity?: ConsequenceSeverity;
  showAccentBar?: boolean;
}

const DEFAULT_CONFIG: Required<PanelConfig> = {
  width: 500,
  maxWidth: 600,
  padding: 24,
  severity: 'neutral',
  showAccentBar: true,
};

export class Panel extends Container {
  private config: Required<PanelConfig>;
  private background: Graphics;
  private accentBar: Graphics;
  private contentContainer: Container;
  private currentHeight = 0;

  constructor(config: PanelConfig = {}) {
    super();

    this.config = { ...DEFAULT_CONFIG, ...config };

    this.background = new Graphics();
    this.addChild(this.background);

    this.accentBar = new Graphics();
    this.addChild(this.accentBar);

    this.contentContainer = new Container();
    const contentX = this.config.showAccentBar ? this.config.padding + 4 : this.config.padding;
    this.contentContainer.position.set(contentX, this.config.padding);
    this.addChild(this.contentContainer);

    this.drawBackground();
  }

  private drawBackground(): void {
    const { width, padding, severity, showAccentBar } = this.config;
    const height = Math.max(this.currentHeight + padding * 2, 80);
    const styles = theme.severityStyles[severity];

    this.background.clear();

    // Shadow
    this.background.roundRect(0, 4, width, height, theme.radii.lg);
    this.background.fill({ color: 0x000000, alpha: 0.15 });

    // Main background
    this.background.roundRect(0, 0, width, height, theme.radii.lg);
    this.background.fill(styles.bg);

    // Border
    this.background.roundRect(0, 0, width, height, theme.radii.lg);
    this.background.stroke({ width: 1, color: styles.border, alpha: 0.3 });

    // Accent bar
    if (showAccentBar) {
      this.accentBar.clear();
      this.accentBar.roundRect(0, 8, 4, height - 16, 2);
      this.accentBar.fill(styles.accent);
    }
  }

  setSeverity(severity: ConsequenceSeverity): void {
    this.config.severity = severity;
    this.drawBackground();
  }

  addTitle(text: string): Text {
    const style = new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.xl,
      fontWeight: theme.typography.weights.semibold,
      fill: theme.colors.textPrimary,
      wordWrap: true,
      wordWrapWidth: this.getContentWidth(),
      letterSpacing: theme.typography.letterSpacing.normal,
    });

    const title = new Text({ text, style });
    title.position.set(0, this.currentHeight);
    this.contentContainer.addChild(title);

    this.currentHeight += title.height + 12;
    this.drawBackground();

    return title;
  }

  addText(text: string): Text {
    const style = new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.base,
      fill: theme.colors.textSecondary,
      wordWrap: true,
      wordWrapWidth: this.getContentWidth(),
      lineHeight: theme.typography.sizes.base * theme.typography.lineHeight.relaxed,
    });

    const textObj = new Text({ text, style });
    textObj.position.set(0, this.currentHeight);
    this.contentContainer.addChild(textObj);

    this.currentHeight += textObj.height + 12;
    this.drawBackground();

    return textObj;
  }

  addPrinciple(text: string): Container {
    const container = new Container();
    container.position.set(0, this.currentHeight);

    const contentWidth = this.getContentWidth();

    // Create text first to measure
    const style = new TextStyle({
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.sm,
      fontStyle: 'italic',
      fill: theme.colors.safe,
      wordWrap: true,
      wordWrapWidth: contentWidth - 32,
      lineHeight: theme.typography.sizes.sm * theme.typography.lineHeight.relaxed,
    });

    const textObj = new Text({ text: `"${text}"`, style });
    textObj.position.set(16, 12);

    const height = textObj.height + 24;

    // Background
    const bg = new Graphics();
    bg.roundRect(0, 0, contentWidth, height, theme.radii.md);
    bg.fill({ color: theme.severityStyles.positive.bg, alpha: 0.8 });

    // Left accent
    bg.roundRect(0, 0, 4, height, 2);
    bg.fill(theme.colors.safe);

    container.addChild(bg);
    container.addChild(textObj);
    this.contentContainer.addChild(container);

    this.currentHeight += height + 16;
    this.drawBackground();

    return container;
  }

  addSpacer(height = 16): void {
    this.currentHeight += height;
    this.drawBackground();
  }

  private getContentWidth(): number {
    const accentOffset = this.config.showAccentBar ? 8 : 0;
    return this.config.width - this.config.padding * 2 - accentOffset;
  }

  clearContent(): void {
    this.contentContainer.removeChildren();
    this.currentHeight = 0;
    this.drawBackground();
  }

  getDimensions(): { width: number; height: number } {
    return {
      width: this.config.width,
      height: this.currentHeight + this.config.padding * 2,
    };
  }

  setWidth(width: number): void {
    this.config.width = Math.min(width, this.config.maxWidth);
    this.drawBackground();
  }
}
